import { useField, useFormikContext } from 'formik';
import { memo, useCallback } from 'react';
import { GMFormAutocompleteProps, GMAutocomplete } from '..';
import { ErrorLevel } from '../../../../API/Enums';
import { GoMotoComponent } from '../../../../API/GoMoto.types';

/**
 * Autocomplete for use in a Formik.
 *
 * Sets the value, onChange, error, & helperText internally
 */
const FormAutocomplete: GoMotoComponent<GMFormAutocompleteProps> = memo(({
    validateBeforeSubmit,
    helperText,
    warningText,
    onBlur: propsOnBlur,
    ...props
}) => {
    const [field, meta] = useField(props.name);
    const { setFieldValue, submitCount, setFieldTouched } = useFormikContext();

    const onChange = useCallback((newValue: string) => {
        setFieldValue(props.name, newValue);
    }, [props.name, setFieldValue]);

    const onBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
        setFieldTouched(props.name, true);
        if (propsOnBlur) {
            propsOnBlur(e);
        }
    }, [propsOnBlur, setFieldTouched, props.name]);

    const getErrorLevel = (): ErrorLevel => {
        if (!!meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return ErrorLevel.Error;
        }

        if (!!warningText) {
            return ErrorLevel.Warning;
        }

        return ErrorLevel.None;
    };

    const getHelperText = (): GMFormAutocompleteProps['helperText'] => {
        if (meta.error && meta.touched && (validateBeforeSubmit || submitCount > 0)) {
            return meta.error;
        }

        if (warningText) {
            return warningText;
        }

        return helperText;
    };

    return (
        <GMAutocomplete
            value={field.value}
            onChange={onChange}
            error={getErrorLevel()}
            helperText={getHelperText()}
            onBlur={onBlur}
            {...props}
        />
    );
});

export default FormAutocomplete;